import { useState } from "react";
import { Link } from "react-router-dom";
import { formatDateTime } from "../../api/dates";
import { useBatch, useIngestContext, useProgress, useUploadBatch, type Batch, type IngestContext } from "../../api/delivery";
import { PageHead, StatusChip } from "../../components/ui";
import "../../styles/upload.css";
import BatchReview, { committedText, stateTone, type CommitResult } from "./BatchReview";
import OtherImports, { type LastImports } from "./OtherImports";
import { ChooseCard, ReadingCard } from "./UploadCard";

/** The Upload screen's context, with what the side cards say about the last
 * workbook imports. `delivery.ts` is shared with the dashboard and workflow
 * screens, so the extra field is described here, where it is read. */
type UploadContext = IngestContext & { last_imports?: LastImports };

function token(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const STEPS = ["Choose PDFs", "Read", "Review and commit"] as const;

/** Choose / Read / Review and commit, with the step that is current marked
 * and those behind it ticked. `done` ticks all three, once a batch is in. */
function Steps({ at, done = false }: { at: number; done?: boolean }) {
  return (
    <ol className="steps" aria-label="Upload steps">
      {STEPS.map((label, index) => {
        const finished = done || index < at;
        const current = !done && index === at;
        return (
          <li key={label} className={current ? "step current" : finished ? "step done" : "step"} aria-current={current ? "step" : undefined}>
            <span className="step-num">{finished ? "✓" : index + 1}</span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

/** Upload: drop the City's QCP documents, watch them being read, review what
 * was extracted and commit the rows that are right; the project schedule and
 * the approved rate card beside it; and what every batch before did. */
export default function IngestPage() {
  const context = useIngestContext();
  const upload = useUploadBatch();
  const [contractId, setContractId] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [sent, setSent] = useState(0);
  const [progressToken, setProgressToken] = useState<string | null>(null);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [committed, setCommitted] = useState<{ id: number; result: CommitResult } | null>(null);
  const progress = useProgress(progressToken);

  const data = context.data as UploadContext | undefined;
  const contracts = data?.contracts ?? [];
  const contract = contractId || (contracts.length === 1 ? String(contracts[0].id) : "");

  function start() {
    const t = token();
    setProgressToken(t);
    setBatch(null);
    setCommitted(null);
    setSent(files.length);
    upload.mutate(
      { contractId: Number(contract), files, token: t },
      {
        onSuccess: (made) => {
          setBatch(made);
          setFiles([]);
        },
      },
    );
  }

  function reset() {
    setBatch(null);
    setCommitted(null);
    setProgressToken(null);
    upload.reset();
  }

  const step = committed ? 3 : batch ? 2 : upload.isPending ? 1 : 0;

  return (
    <div className="upload-page">
      <div className="upload-main">
        <PageHead title="Upload QCP documents" sub="PDFs from the City. Each becomes a site once you commit it." />
        <Steps at={Math.min(step, 2)} done={step === 3} />

        {context.isError ? (
          <p className="notice bad" role="alert">
            Could not load the upload screen: {context.error.message}
          </p>
        ) : null}

        {step === 0 ? (
          <ChooseCard
            contracts={contracts}
            contract={contract}
            onContract={setContractId}
            files={files}
            onFiles={setFiles}
            onStart={start}
            busy={upload.isPending}
          />
        ) : null}
        {step === 1 ? <ReadingCard progress={progress.data} sent={sent} /> : null}
        {upload.isError ? (
          <p className="notice bad" role="alert">
            {upload.error.message}
          </p>
        ) : null}
        {progress.data?.error ? (
          <p className="notice bad" role="alert">
            {progress.data.error}
          </p>
        ) : null}
        {step === 2 && batch ? (
          <BatchReview batch={batch} onCommitted={(result) => setCommitted({ id: batch.batch.id, result })} />
        ) : null}
        {step === 3 && committed ? (
          <section className="upload-done" role="status">
            <div>
              <strong>Batch #{committed.id} committed</strong>
              <p>{committedText(committed.result)} Flags were raised where a check failed.</p>
            </div>
            <Link className="btn btn-secondary" to="/sites">
              See the sites
            </Link>
            <button type="button" className="btn btn-primary" onClick={reset}>
              Upload more
            </button>
          </section>
        ) : null}

        <EarlierBatches history={data?.history} loading={context.isPending} />
      </div>
      <OtherImports contracts={contracts} last={data?.last_imports} />
    </div>
  );
}

function EarlierBatches({ history, loading }: { history: IngestContext["history"] | undefined; loading: boolean }) {
  return (
    <section className="card" aria-label="Earlier batches">
      <div className="card-head">
        <h2>Earlier batches</h2>
      </div>
      {loading ? (
        <p className="empty-state">Loading…</p>
      ) : !history || history.length === 0 ? (
        <p className="empty-state">No batches yet. The first upload will appear here.</p>
      ) : (
        <div className="upload-table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Batch</th>
                <th>Uploaded</th>
                <th>State</th>
                <th className="num">Files</th>
                <th className="num">Loaded</th>
                <th className="num">With issues</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.batch_id}>
                  <td>
                    <Link to={`/ingest/${row.batch_id}`}>#{row.batch_id}</Link>
                  </td>
                  <td className="muted" title={row.uploaded_by_email ?? undefined}>
                    {formatDateTime(row.uploaded_at)}
                  </td>
                  <td>
                    <StatusChip tone={stateTone(row.state)}>{row.state}</StatusChip>
                  </td>
                  <td className="num">{row.file_count}</td>
                  <td className="num">{row.loaded}</td>
                  <td className={row.with_issues > 0 ? "num upload-warn-num" : "num muted"}>{row.with_issues}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** `/ingest/:id`: one batch's review screen, reached from Earlier batches or
 * a link. A draft can be committed here as on the Upload screen. */
export function BatchPage({ id }: { id: number }) {
  const batch = useBatch(id);
  const draft = batch.data?.batch.state === "draft";
  return (
    <div className="upload-page single">
      <div className="upload-main">
        <PageHead
          title={`Batch #${id}`}
          sub="What was read from each document, and what committing it does."
          actions={
            <Link className="go-link" to="/ingest">
              ← Upload
            </Link>
          }
        />
        {batch.data ? <Steps at={2} done={!draft} /> : null}
        {batch.isPending ? <p className="empty-state">Loading batch…</p> : null}
        {batch.isError ? (
          <p className="notice bad" role="alert">
            {batch.error.status === 404 ? "There is no batch with that number." : `Could not load the batch: ${batch.error.message}`}
          </p>
        ) : null}
        {batch.data ? <BatchReview key={batch.data.batch.id} batch={batch.data} /> : null}
      </div>
    </div>
  );
}
